import { useCallback, useContext, useEffect, useState } from "react";
import { roundToDecimal } from "../../../utils/roundToDecimal";
import { AppContext } from "../../../contexts/AppContext";
import { REGIONS } from "../../../constants/constants";

/**
 * A custom React hook that returns groundwater assessment facts.
 * @category Hooks
 * @component
 * @param {Object} options - The options object.
 * @param {Object} options.remoteNationalData - The remote national data object.
 * @returns {Object} The groundwater assessment facts object.
 */
const useGroundwaterFacts = ({ remoteNationalData }) => {
  const [groundwaterFacts, setGroundwaterFacts] = useState(null);
  const { region } = useContext(AppContext);

  const gwValues = (yieldType, year) => {
    try {
      if (year === "2020") {
        if (REGIONS.africa.includes(region)) {
          return optimisticAndPessimisticValues(yieldType, year);
        } else {
          return {
            value_2020: `${roundToDecimal(
              remoteNationalData.groundwater[yieldType][year],
              1
            )}`,
          };
        }
      } else if (year === "2030") {
        if (REGIONS.africa.includes(region)) {
          return optimisticAndPessimisticValues(yieldType, year);
        }
      } else {
        return optimisticAndPessimisticValues(yieldType, year);
      }
    } catch (error) {
      console.error("error", error);
      return {};
    }
  };

  const optimisticAndPessimisticValues = (yieldType, year) => {
    const optimisticValueName = `value_${year}_optimistic`;
    const pessimisticValueName = `value_${year}_pessimistic`;
    return {
      [optimisticValueName]: roundToDecimal(
        remoteNationalData.groundwater[yieldType][year].optimistic,
        1
      ),
      [pessimisticValueName]: roundToDecimal(
        remoteNationalData.groundwater[yieldType][year].pessimistic,
        1
      ),
    };
  };

  /**
   * A function that returns an object containing groundwater assessment facts.
   * @returns {Object} The groundwater assessment facts object.
   */

  const getGroundwaterFacts = useCallback(() => {
    return {
      heading: "Groundwater assessment",
      main: [
        {
          title: "Groundwater sustainable yield (Extreme)",
          ...gwValues("extreme", "2020"),
          ...gwValues("extreme", "2030"),
          ...gwValues("extreme", "2050"),
          unit: "mm/yr",
          annotation: "",
        },
        {
          title: "Groundwater sustainable yield (Medium)",
          ...gwValues("medium", "2020"),
          ...gwValues("medium", "2030"),
          ...gwValues("medium", "2050"),
          unit: "mm/yr",
          annotation: "",
        },

        {
          title: "Groundwater sustainable yield (Conservative)",
          ...gwValues("conservative", "2020"),
          ...gwValues("conservative", "2030"),
          ...gwValues("conservative", "2050"),
          unit: "mm/yr",
          annotation: "",
        },
      ],
      preFootnotes: [{ label: "gwFirst" }],
      footnotes: [
        { label: "gwFirst" },
        { label: "gwSecond" },
        { label: "gwThird" },
        { label: "gwFourth" },
        { label: "gwFifth" },
        { label: "gwSixth" },
      ],
      references: [
        {
          label:
            "https://theconversation.com/groundwater-reserves-in-africa-may-be-more-resilient-to-climate-change-than-first-thought-120948; last access: 1 November 2023.",
          referenceAnnotation: 1,
        },
        {
          label:
            "Calow, R.C., MacDonald, A.M., Nicol, A.L., and Robins, N.S., 2010. Ground water security and drought in Africa: linking availability, access, and demand. Groundwater, 48 (2), 246–256.",
          referenceAnnotation: 2,
        },
        {
          label:
            "Calow, R.C., Robins, N.S., Macdonald, A.M., Macdonald, D.M.J., Gibbs, B.R., Orpen, W.R.G., Mtembezeka, P., Andrews, A.J., and Appiah, S.O., 1997. Groundwater Management in Drought-prone Areas of Africa. International Journal of Water Resources Development, 13 (2), 241–262.",
          referenceAnnotation: 3,
        },
        {
          label:
            "MacDonald, A.M., Bonsor, H.C., Dochartaigh, B.É.Ó., and Taylor, R.G., 2012. Quantitative maps of groundwater resources in Africa. Environmental Research Letters, 7 (2), 24009.",
          referenceAnnotation: 4,
        },
        {
          label:
            "Bayat, B., Oloruntoba, B., Montzka, C., Vereecken, H., and Hendricks Franssen, H.-J., 2023. Implications for sustainable water consumption in Africa by simulating five decades (1965-2014) of groundwater recharge. Journal of Hydrology, 130288.",
          referenceAnnotation: 5,
        },
        {
          label:
            "Lawrence, D.M., Fisher, R.A., Koven, C.D., Oleson, K.W., Swenson, S.C., Bonan, G., Collier, N., Ghimire, B., van Kampenhout, L., Kennedy, D., and others, 2019. The Community Land Model version 5: Description of new features, benchmarking, and impact of forcing uncertainty. Journal of Advances in Modeling Earth Systems, 11 (12), 4245–4287.",
          referenceAnnotation: 6,
        },
        {
          label: "https://cordex.org/; last access: 1 November 2023.",
          referenceAnnotation: 7,
        },
        {
          label:
            "Sutanudjaja, E.H., Van Beek, R., Wanders, N., Wada, Y., Bosmans, J.H.C., Drost, N., Van Der Ent, R.J., De Graaf, I.E.M., Hoch, J.M., De Jong, K., and others, 2018. PCR-GLOBWB 2: a 5 arcmin global hydrological and water resources model. Geoscientific Model Development, 11 (6), 2429–2453.",
          referenceAnnotation: 8,
        },
      ],
    };
  }, [remoteNationalData]);

  useEffect(() => {
    try {
      if (!remoteNationalData) return;
      setGroundwaterFacts(getGroundwaterFacts());
    } catch (error) {
      console.error("error", error);
    }
  }, [remoteNationalData]);
  return { groundwaterFacts };
};

export default useGroundwaterFacts;
